import { Dialog, Dropdown } from '@cimplico/components';
import { useState } from 'react';
import { useDeleteCalculation } from '../../../queries/use-delete-calculation';
import { Calculation } from '../../../types/worksheet';
import { useCarryForward } from '../../../queries/use-carry-forward';
import {
    CarryForwardCalculationResultMetadata,
    ResourceType,
    CarryForwardLinkedResourceType,
    CarryForwardResultDB,
} from '../../../types/carry-forward';
import { toastNotification } from '../../../components/toast/Toast';
import { CWTBehaviourEnum } from '../../../types/custom-worksheet-v2';
import plural from '../../../utilities/plural';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../../intercom-events';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';

type CalculationActionsProps = {
    calculation: Calculation;
    worksheetId: string;
    priorDetails?: {
        priorWorkpaperId: string;
        disableCarryForward: boolean;
        disableCarryForwardHint?: string;
    };
};

function CalculationActions({ calculation, worksheetId, priorDetails }: CalculationActionsProps): JSX.Element {
    const [action, setAction] = useState<string>();
    const { id: currentWorkpaperId } = useWorkpaperContext();
    return (
        <div className="absolute right-0" onClick={(e): void => e.stopPropagation()}>
            {action === 'REMOVE' && (
                <ConfirmDelete calculation={calculation} worksheetId={worksheetId} onClose={(): void => setAction(undefined)} />
            )}
            {action === 'CARRY_FORWARD' && (
                <ConfirmCarryForward
                    priorWorkpaperId={priorDetails!.priorWorkpaperId}
                    currentWorkpaperId={currentWorkpaperId}
                    calculation={calculation}
                    onClose={(): void => setAction(undefined)}
                />
            )}
            <Dropdown>
                {!priorDetails && (
                    <Dropdown.Option
                        label="Remove from worksheet"
                        onClick={(_, e): void => {
                            e?.stopPropagation();
                            setAction('REMOVE');
                        }}
                    />
                )}
                {priorDetails && (
                    <Dropdown.Option
                        label="Carry forward"
                        onClick={(_, e): void => {
                            e?.stopPropagation();
                            setAction('CARRY_FORWARD');
                        }}
                        disabled={priorDetails.disableCarryForward}
                        disabledHint={
                            priorDetails.disableCarryForward && priorDetails.disableCarryForwardHint
                                ? priorDetails.disableCarryForwardHint
                                : undefined
                        }
                    />
                )}
            </Dropdown>
        </div>
    );
}

function ConfirmDelete({ calculation, worksheetId, onClose }: CalculationActionsProps & { onClose: () => void }): JSX.Element {
    const { mutate, isLoading } = useDeleteCalculation();

    function handleRemove(): void {
        mutate(
            { id: calculation.id, worksheetId },
            {
                onSettled() {
                    onClose();
                },
            }
        );
    }

    return (
        <Dialog
            description="Are you sure you want to remove this calculation from the worksheet? Doing so will unlink any associated entries. You can re-link the calculation at any time."
            isOpen
            primaryAction="Remove"
            title={`Delete ${calculation.name}`}
            type="error"
            onClose={onClose}
            secondaryAction="Cancel"
            onSecondaryActionClick={onClose}
            isLoading={isLoading}
            onPrimaryActionClick={handleRemove}
        />
    );
}

function ConfirmCarryForward({
    priorWorkpaperId,
    currentWorkpaperId,
    calculation,
    onClose,
}: {
    priorWorkpaperId: string;
    currentWorkpaperId: string;
    calculation: Calculation;
    onClose: () => void;
}): JSX.Element {
    const { mutate, isLoading } = useCarryForward();
    const { trackEvent } = useIntercom();

    function handleCarryForward(): void {
        const payload = {
            priorWorkpaperId,
            currentWorkpaperId,
            payloads: [{ type: ResourceType.CALCULATION, priorResourceId: calculation.id }],
        };
        mutate(payload, {
            onSuccess: (res) => {
                const carryForwardCalcResult = res[0];
                const metadata = carryForwardCalcResult.metadata
                    ? (carryForwardCalcResult.metadata as CarryForwardCalculationResultMetadata)
                    : undefined;

                const successResult = carryForwardCalcResult.outcome === 'success';
                const linkedResources = getLinkedResourcesResults(metadata);
                const linkedEntries = linkedResources?.linkedResources.linkedEntries;

                if (successResult) {
                    trackCarryForwardIntercomEvent(carryForwardCalcResult, currentWorkpaperId);
                }

                const { toastType, toastTitle, toastMessage } = generateCarryForwardToastParams(
                    successResult,
                    calculation,
                    metadata,
                    linkedEntries
                );
                onClose();
                toastNotification(toastType, toastTitle, toastMessage);
            },
            onError: (res) => {
                onClose();
                toastNotification('error', `Failed to carry forward ${calculation.name}. ${res.response?.data ?? ''}`);
            },
        });
    }

    // Structured to allow addition of new linkedResources as implemented.
    function getLinkedResourcesResults(metadata?: CarryForwardCalculationResultMetadata):
        | {
              linkedResources: { linkedEntries: { linkedEntriesCount: number; linkedEntriesSuccessCount: number } };
          }
        | undefined {
        if (!metadata?.linkedResources?.length) return undefined;

        return metadata.linkedResources.reduce(
            (acc, res) => {
                if (res.resourceType === CarryForwardLinkedResourceType.ENTRY) {
                    acc.linkedResources.linkedEntries.linkedEntriesCount++;
                    if (res.resourceType === CarryForwardLinkedResourceType.ENTRY && res.outcome === 'success')
                        acc.linkedResources.linkedEntries.linkedEntriesSuccessCount++;
                }
                return acc;
            },
            { linkedResources: { linkedEntries: { linkedEntriesCount: 0, linkedEntriesSuccessCount: 0 } } }
        );
    }

    function trackCarryForwardIntercomEvent(cfCalcResult: CarryForwardResultDB[number], currentWorkpaperId: string): void {
        trackEvent(
            intercomEvents.carryForward.carryForwardCalculation.event,
            intercomEvents.carryForward.carryForwardCalculation.metaData(
                currentWorkpaperId,
                cfCalcResult.currentResourceId!,
                cfCalcResult.priorResourceId
            )
        );
    }

    function generateCarryForwardToastParams(
        isSuccess: boolean,
        calculation: Calculation,
        metadata?: CarryForwardCalculationResultMetadata,
        linkedEntries?: { linkedEntriesCount: number; linkedEntriesSuccessCount: number }
    ): { toastType: 'success' | 'error'; toastTitle: string; toastMessage: string } {
        const toastType = isSuccess ? 'success' : 'error';
        const toastTitle = isSuccess ? 'Carried Forward' : 'Carry Forward Error';
        let toastMessage = '';

        if (isSuccess) {
            toastMessage = `${calculation.name} was carried forward.`;
            // if (linkedEntries && linkedEntries.linkedEntriesCount > 0) {
            //     const { linkedEntriesCount, linkedEntriesSuccessCount } = linkedEntries;
            //     const countText =
            //         linkedEntriesSuccessCount !== linkedEntriesCount
            //             ? `${linkedEntriesSuccessCount} of ${linkedEntriesCount}`
            //             : `${linkedEntriesSuccessCount}`;
            //     toastMessage += ` ${countText} linked ${plural(linkedEntriesCount, 'entry', 'entries')} carried forward successfully`;
            // }
        } else {
            toastMessage = `Failed to carry forward ${calculation.name}. ${metadata?.outcomeMessage ?? ''}`;
        }
        return { toastType, toastTitle, toastMessage };
    }

    return (
        <Dialog
            description={
                calculation.behaviour === CWTBehaviourEnum.COPY
                    ? `Are you sure you want to carry forward ${calculation.name} to the current worksheet?`
                    : `This is a SHARED calculation and may appear on multiple worksheets in the prior workpaper. Continuing will add it (and its linked entries) to corresponding worksheets in the current workpaper that have been linked to those prior worksheets. Unlinked worksheets will be skipped. Ensure you have linked relevant prior worksheets before confirming.`
            }
            isOpen
            primaryAction="Confirm"
            title={`Carry Forward ${calculation.name}?`}
            type="info"
            onClose={onClose}
            secondaryAction="Cancel"
            onSecondaryActionClick={onClose}
            isLoading={isLoading}
            onPrimaryActionClick={handleCarryForward}
        />
    );
}

export default CalculationActions;
